import { Component, OnInit, AfterViewInit } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InquiryService } from 'src/app/services/inquiry/inquiry.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';

declare var KTMenu: any;

@Component({
  selector: 'app-cm-inquiries',
  templateUrl: './cm-inquiries.component.html',
  styleUrls: ['./cm-inquiries.component.scss']
})
export class CmInquiriesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  inquiries: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;

  constructor(
    private _inquiryService: InquiryService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadInquiries();
  }

  ngAfterViewInit(): void {
    this.refreshMenus();
  }

  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  loadInquiries(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    this.subscriptions.push(
      this._inquiryService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.inquiries = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.inquiries = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading inquiries:', error);
          this.inquiries = [];
          this.resetPagination();
          this.isLoading = false;
          this.refreshMenus();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadInquiries(page, this.searchTerm);
  }

  // Button click handlers
  onReplyClick(inquiry: any): void {
    console.log('Reply clicked for inquiry:', inquiry);

    // Set the selected inquiry for the modal
    (window as any).selectedInquiryForReply = inquiry;

    // Open the modal using Bootstrap
    const modalElement = document.getElementById('kt_modal_send_inquiry_reply');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onCloseInquiryClick(inquiry: any): void {
    console.log('Close Inquiry clicked for inquiry:', inquiry);

    // Show SweetAlert confirmation
    const swalWithBootstrapButtons = (window as any).Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-danger'
      },
      buttonsStyling: false
    });

    swalWithBootstrapButtons.fire({
      title: 'Close Inquiry?',
      text: 'Are you sure you want to close this inquiry? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, close it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then((result: any) => {
      if (result.isConfirmed) {
        this.closeInquiry(inquiry);
      }
    });
  }

  private closeInquiry(inquiry: any): void {
    const updateData = {
      status: 'closed'
    };

    this.subscriptions.push(
      this._inquiryService.UpdateData(inquiry._id, updateData).subscribe({
        next: (response: any) => {
          if (response.status) {
            // Show success message
            (window as any).Swal.fire({
              title: 'Closed!',
              text: 'The inquiry has been closed successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
              customClass: {
                confirmButton: 'btn btn-primary'
              },
              buttonsStyling: false
            });

            // Reload inquiries to show updated data
            this.loadInquiries(this.currentPage, this.searchTerm);
          } else {
            // Show error message
            (window as any).Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to close inquiry. Please try again.',
              icon: 'error',
              confirmButtonText: 'OK',
              customClass: {
                confirmButton: 'btn btn-primary'
              },
              buttonsStyling: false
            });
          }
        },
        error: (error) => {
          console.error('Error closing inquiry:', error);
          // Show error message
          (window as any).Swal.fire({
            title: 'Error!',
            text: 'An error occurred while closing the inquiry. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: {
              confirmButton: 'btn btn-primary'
            },
            buttonsStyling: false
          });
        }
      })
    );
  }

  onForwardInquiryClick(inquiry: any): void {
    console.log('Forward Inquiry clicked for inquiry:', inquiry);

    // Set the selected inquiry for the modal
    (window as any).selectedInquiryForForward = inquiry;

    // Open the modal using Bootstrap
    const modalElement = document.getElementById('kt_modal_inquiry_sales_person');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onTranslateClick(inquiry: any): void {
    let message = inquiry.message;
    if (!message) {
      return;
    }
    let url = 'https://translate.google.com/?sl=auto&tl=en&text=' + encodeURIComponent(message);
    window.open(url, '_blank');
  }

  onStockClick(inquiry: any): void {
    console.log('Stock clicked for inquiry:', inquiry);
  }

  onOnePriceClick(inquiry: any): void {
    console.log('One Price clicked for inquiry:', inquiry);
  }

  onAuctionClick(inquiry: any): void {
    console.log('Auction clicked for inquiry:', inquiry);
  }

  // Method called when reply is sent successfully
  onReplyAdded(): void {
    // Reload the inquiries to show updated data
    this.loadInquiries(this.currentPage, this.searchTerm);
  }

  // Method called when inquiry is forwarded successfully
  onInquiryForwarded(): void {
    // Reload the inquiries to show updated data
    this.loadInquiries(this.currentPage, this.searchTerm);
  }

  // Helper methods for template
  getVehicleInfo(inquiry: any): string {
    if (inquiry.vehicle) {
      return `${inquiry.vehicle.company_name || '-'} / ${inquiry.vehicle.name || '-'} / ${inquiry.vehicle.year || '-'} / ${inquiry.vehicle.car_no || '-'}`;
    } else if (inquiry.car) {
      return `${inquiry.car.company_name || '-'} / ${inquiry.car.name || '-'} / ${inquiry.car.year || '-'} / ${inquiry.car.inventory_no || '-'}`;
    }
    return '-';
  }

  getVehicleDetails(inquiry: any): string {
    const car = inquiry.car;
    if (car) {
      return `${car.transmission || '-'} | ${car.fuel_type || '-'} | ${car.engine || '-'} | ${car.mileage || '-'}`;
    }
    return '-';
  }

  getBudget(inquiry: any): string {
    return inquiry.car?.price || '-';
  }

  getInquirySource(inquiry: any): string {
    if (inquiry.vehicle) {
      return 'Stock';
    } else if (inquiry.car) {
      return inquiry.car.type || '-';
    }
    return '-';
  }

  getReplyStatus(inquiry: any): string {
    return inquiry.status;
  }

  getForwardStatus(inquiry: any): string {
    return inquiry.sales_person_id ? 'Forwarded' : 'Unforwarded';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  trackByInquiryId(index: number, inquiry: any): string {
    return inquiry._id || index;
  }
}
